/* Styles */
import styles from 'styles/login.module.scss';

/* Core Dependencies */
import { useState, useEffect } from 'react';
import {
  HashRouter,
  Route,
  Switch,
  Redirect
} from "react-router-dom";
import { Auth, Hub } from 'aws-amplify';

/* i18n Dependencies */
import { useIntl } from 'react-intl';

/* App Theme and UI Dependencies */
import { ThemeProvider } from '@material-ui/styles';
import theme from 'styles/ui-theme';
import Layout from 'components/layout';

/* API */
import { getProfile, postProfile } from 'lib/API';

/* Analytics */
import { trackEvent } from 'lib/analytics';
import TimeMe from "timeme.js";
import { deviceDetect } from "react-device-detect";

/* Redux */
import { useDispatch, useSelector } from 'react-redux';
import { setCareers, setGoal } from 'app/slices/careersSlice';
import { setJobs, setCurrentSoc } from 'app/slices/jobsSlice';
import { setTraining } from 'app/slices/trainingSlice';
import {
  setExperienceSkills,
  setExperienceIndustry,
  setExperienceForm,
  setExperienceHasResume
} from 'app/slices/experienceSlice';
import { setRatings } from 'app/slices/ratingsSlice';
import {
  setLoading,
  setDrawerOpen,
  setNeedsContactInfo,
  setNeedsOnboarding,
  setPreferredName,
  setLoadingRecommendations,
  setUnrecoverableError
} from 'app/slices/appSlice';

/* UI Components */
import LoginFlow from 'components/forms/LoginFlow.jsx';
import RecommendationsHeader from 'components/RecommendationsHeader';
import Careers from 'components/Careers';
import Jobs from 'components/Jobs';
import Training from 'components/Training';
import Experience from 'components/Experience';
import Ratings from 'components/Ratings';
import Drawer from "components/Drawer";
import Loading from 'components/Loading';
import LoadingRecommendations from 'components/LoadingRecommendations';
import OnboardingHeader from 'components/OnboardingHeader';
import OnboardingContact from 'components/forms/OnboardingContact';
import OnboardingResumeUpload from "components/forms/OnboardingResumeUpload";
import OnboardingIndustry from "components/forms/OnboardingIndustry";
import OnboardingExperience from "components/forms/OnboardingExperience";
import OnboardingGoals from "components/forms/OnboardingGoals";
import ErrorMessage from 'components/ErrorMessage';
import SignOutMessage from 'components/SignOutMessage';
import TermsOfUse from 'components/TermsOfUse';
import CreditsCitations from 'components/CreditsCitations';
import Chat from 'components/Chat';
import { useSnackbar } from 'notistack';

function App({ language }) {

  const [authState, setAuthState] = useState();
  const [user, setUser] = useState();

  const dispatch = useDispatch();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const {
    drawerOpen,
    loading,
    loadingRecommendations,
    needsContactInfo,
    needsOnboarding,
    preferredName,
    unrecoverableError
  } = useSelector((state) => state.app);

  const handleDrawerOpen = (e) => {
    e.preventDefault();
		dispatch(setDrawerOpen(true));
	};

	const handleDrawerClose = () => {
		dispatch(setDrawerOpen(false));
	};

  const signOut = async () => {
    dispatch(setLoading(true));
    await Auth.signOut({global:true});
    setUser(null);
    setAuthState('signedout');
    dispatch(setLoading(false));
  }

  const loadUser = () => {
    dispatch(setLoading(true));
    trackEvent('LOAD', {
      device: deviceDetect(),
      referrer: document.referrer,
      path: window.location.pathname,
      language: navigator.language || navigator.userLanguage,
      localtime: Date()
    });
    getProfile()
    .then(function(response) {
      // Store response in redux
      if (typeof(response.id) !== 'undefined') {
        dispatch(setPreferredName(response.id.value.preferred_name));
      }
      if (typeof(response.industry) !== 'undefined') {
        dispatch(setExperienceIndustry(response.industry.value));
      }
      if (typeof(response.goal) !== 'undefined') {
        dispatch(setGoal(response.goal.value.goal));
      }
      if (typeof(response.experience) !== 'undefined') {
        dispatch(setExperienceForm(response.experience.value));
        dispatch(setCurrentSoc(response.experience.value.soc_code));
        dispatch(setExperienceHasResume(response.experience.value.has_resume));
      }
      if (typeof(response.ratings) !== 'undefined') {
        dispatch(setRatings(response.ratings));
      }
      // Check for existing recommendation.
      let today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      if (
        typeof(response.careers) !== 'undefined' &&
        typeof(response.jobs) !== 'undefined' &&
        typeof(response.training) !== 'undefined' &&
        typeof(response.careers.timestamp) !== 'undefined' &&
        response.careers.timestamp.slice(0, 8) === today
      ) {
        // Use existing recommendation if it was generated today.
        enqueueSnackbar(
          intl.formatMessage({id:'app.loadedRecommendations', description:'string'}),
          {"variant": "success"}
        );
        dispatch(setCareers(response.careers.value));
        dispatch(setJobs(response.jobs.value));
        dispatch(setTraining(response.training.value));
        dispatch(setExperienceSkills(response.skills.value));
        dispatch(setLoading(false));
      } else if (typeof(response.experience) !== 'undefined' ) {
        // Regenerate recommendation if experience is present
        dispatch(setLoadingRecommendations(true));
        enqueueSnackbar(
          intl.formatMessage({id:'app.updatingRecommendations', description:'string'}),
          {"variant": "success"}
        );
        let values = {
          ...response.experience.value,
          action: 'recommend',
          language: language
        };
        if (typeof(response.goal) !== 'undefined') {
          values['goal'] = response.goal.value.goal;
        }
        if (typeof(response.skills) !== 'undefined') {
          values['skills'] = response.skills.value;
        }
        postProfile(values).then(regen_response => {
          dispatch(setCareers(regen_response.careers.value));
          dispatch(setJobs(regen_response.jobs.value));
          dispatch(setTraining(regen_response.training.value));
          dispatch(setExperienceSkills(regen_response.skills.value));
          dispatch(setLoadingRecommendations(false));
          dispatch(setLoading(false));
        }).catch(error => {
          dispatch(setUnrecoverableError(true));
        })
      } else {
        dispatch(setNeedsOnboarding(true));
        dispatch(setLoading(false));
      }
    })
    .catch(function(error) {
      try {
        if (error.response.data.error_message === 'user is not registered') {
          dispatch(setNeedsOnboarding(true));
          dispatch(setNeedsContactInfo(true));
          dispatch(setLoading(false));
        } else {
          dispatch(setUnrecoverableError(true));
        }
      } catch {
        dispatch(setUnrecoverableError(true));
      }
    });
  }

  const updateUser = async authState => {
    try {
      let user = await Auth.currentAuthenticatedUser();
      setAuthState('signedin');
      setUser(user);
      loadUser();
    } catch {
      setUser(null);
    }
  }

  useEffect(() => {
    Hub.listen('auth', updateUser); // listen for login/signup events
    updateUser(); // check manually the first time because we won't get a Hub event
    return () => Hub.remove('auth', updateUser); // cleanup
  }, []);

  useEffect(() => {
    /**
     * Configuration of idle time before we track
     * the user as inactive
     */
    TimeMe.initialize({
      idleTimeoutInSeconds: 300 // seconds
    });
  }, []);

  useEffect(() => {
    TimeMe.callWhenUserLeaves(() => {
      const timeReport = TimeMe.getTimeOnAllPagesInSeconds();
      timeReport.forEach(({ pageName, timeOnPage }) => {
        if (pageName !== 'default-page-name') {
          trackEvent(`TIME_${pageName}_PAGETOTAL`, { timeOnPage: timeOnPage.toString() })
        }
      })
      const timeActiveSession = timeReport.reduce((prev, { timeOnPage }) => prev + timeOnPage, 0);
      trackEvent('TIME_ACTIVE_SESSION', { timeOnPage: timeActiveSession.toString() });
    });

    TimeMe.callWhenUserReturns(() => {
      TimeMe.resetAllRecordedPageTimes();
    });
  }, []);

  const loggedIn = authState === 'signedin' && user;
  const urlPrefix = language === 'en' ? '' : `/${language}`;

  function render() {
    if (authState === 'signedout') {
      return (
        <SignOutMessage />
      );
    } else if (!loggedIn) {
      return (
        <Switch>
          <Route path={['/login/verify', '/:lang/login/verify']}>
            <LoginFlow
              setAuthState={setAuthState}
              setUser={setUser}
              form={'verifyUser'}
            />
          </Route>
          <Route path={['/login/password', '/:lang/login/password']}>
            <LoginFlow
              setAuthState={setAuthState}
              setUser={setUser}
              form={'forgotPassword'}
            />
          </Route>
          <Route path={['/login/signup', '/:lang/login/signup']}>
            <LoginFlow
              setAuthState={setAuthState}
              setUser={setUser}
              form={'signUp'}
            />
          </Route>
          <Route path={['/login', '/:lang/login']}>
            <LoginFlow
              setAuthState={setAuthState}
              setUser={setUser}
              form={'signIn'}
            />
          </Route>
          <Route>
            <Redirect to={`${urlPrefix}/login`} />
          </Route>
        </Switch>
      );
    } else if (unrecoverableError) {
      return (
        <ErrorMessage />
      );
    } else if (loadingRecommendations) {
      return (
        <LoadingRecommendations />
      );
    } else if (loading) {
      return (
        <Loading />
      );
    } else if (needsContactInfo) {
      return (
        <Switch>
          <Route path={['/onboarding/contact-info', '/:lang/onboarding/contact-info']}>
            <>
              <OnboardingHeader />
              <div className={styles.loginFlow}>
                <div className={styles.loginFlow__formContainer}>
                  <OnboardingContact />
                </div>
              </div>
            </>
          </Route>
          <Route>
            <Redirect to={`${urlPrefix}/onboarding/contact-info`} />
          </Route>
        </Switch>
      );
    } else if (needsOnboarding) {
      return (
        <>
          <OnboardingHeader />
          <div className={styles.loginFlow}>
            <div className={styles.loginFlow__formContainer}>
              <Switch>
                <Route path={['/onboarding/resume-upload', '/:lang/onboarding/resume-upload']}>
                  <OnboardingResumeUpload />
                </Route>
                <Route path={['/onboarding/industry', '/:lang/onboarding/industry']}>
                  <OnboardingIndustry />
                </Route>
                <Route path={['/onboarding/experience', '/:lang/onboarding/experience']}>
                  <OnboardingExperience />
                </Route>
                <Route path={['/onboarding/goals', '/:lang/onboarding/goals']}>
                  <OnboardingGoals />
                </Route>
                <Route>
                  <Redirect to={`${urlPrefix}/onboarding/resume-upload`} />
                </Route>
              </Switch>
            </div>
          </div>
        </>
      );
    } else {
      return (
        <>
          <RecommendationsHeader />
          <Switch>
            <Route path={['/careers', '/:lang/careers']} children={<Careers />} />
            <Route path={['/jobs', '/:lang/jobs']} children={<Jobs />} />
            <Route path={['/training', '/:lang/training']} children={<Training />} />
            <Route path={['/experience', '/:lang/experience']} children={<Experience />} />
            <Route path={['/ratings', '/:lang/ratings']} children={<Ratings />} />
            <Route>
              <Redirect to={`${urlPrefix}/careers`} />
            </Route>
          </Switch>
          <Chat preferredName={preferredName} />
        </>
      );
    } 
  }

  return (
    <HashRouter>
      <ThemeProvider theme={theme}>
        <Layout locale={language} handleDrawerOpen={handleDrawerOpen} loggedIn={loggedIn}>
          <Switch>
            <Route path={['/terms-of-use', '/:lang/terms-of-use']}>
              <TermsOfUse />
            </Route>
            <Route path={['/credits-and-citations', '/:lang/credits-and-citations']}>
              <CreditsCitations />
            </Route>
            <Route>
              {render()}
            </Route>
          </Switch>
          <Drawer
            anchor="right"
            open={drawerOpen}
            handleDrawerClose={handleDrawerClose}
            signOut={signOut}
          />
        </Layout>
      </ThemeProvider>
    </HashRouter>
  );
}

export default App;
